
// components/EventCsvUpload.tsx
import React, { useRef, useState } from "react";
import { config } from "../config";
import { useEventsApi } from "../hooks/useEventsApi";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";

type ApiResult = {
    summary: { csv_rows: number; matched: number; param_diff: number; csv_only: number; db_only: number; ambiguous: number };
    matched: any[];
    param_diff: any[];
    csv_only: any[];
    db_only: any[];
    ambiguous: any[];
};

type EventCsvUploadProps = {
  /** Omit outer padding chrome when placed inside Admin accordion. */
  embedded?: boolean;
};

export const EventCsvUpload: React.FC<EventCsvUploadProps> = ({ embedded = false }) => {
    const [file, setFile] = useState<File | null>(null);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState<ApiResult | null>(null);
    const [error, setError] = useState<string | null>(null);
    const { callApi } = useEventsApi();
    const theme = useTheme();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFile(e.target.files?.[0] ?? null);
        setResult(null);
        setError(null);
    };


    const upload = async () => {
        if (!file) return;
        setLoading(true);
        setError(null);
        setResult(null);

        try {
            const form = new FormData();
            form.append("file", file);

            const data = await callApi(
                `${config.apiUrl}/api/compare-csv/?useOldEventCode=true`,
                "POST",
                form,
                true
            );

            setResult(data as ApiResult);
        } catch (e: any) {
            setError(e.message || "Upload failed");
        } finally {
            setLoading(false);
        }
    };

    const pad = embedded ? { px: 2, py: 2 } : { p: 3 };

    return (
        <Box sx={pad}>
            <Typography variant="subtitle2" sx={{ color: theme.palette.text.primary, mb: 1, fontWeight: 600 }}>
                Compare ekOpenVaf export CSV with the database
            </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
                Matches UserEvents by event code (legacy code when enabled). For best contrast, use light UI
                theme when reading tables.
            </Typography>
            <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                <label htmlFor="file-upload">
                    <input
                        ref={fileInputRef}
                        id="file-upload"
                        type="file"
                        accept=".csv,.edf"
                        style={{ display: "none" }}
                        onChange={handleFileChange}
                    />
                    <Button variant="outlined" component="span">Select file</Button>
                </label>
                {file && (
                    <>
                        <Typography variant="body2">{file.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                            CSV
                        </Typography>
                        <Button variant="text" color="secondary" onClick={() => {
                            setFile(null);
                            if (fileInputRef.current) {
                                fileInputRef.current.value = "";
                            }
                        }}>
                            Clear
                        </Button>
                    </>
                )}
                <Button
                    variant="contained"
                    color="primary"
                    onClick={upload}
                    disabled={!file || loading}
                    component="span"
                >
                    {loading ? <CircularProgress size={24} color="inherit" /> : "Run compare"}
                </Button>
            </Box>
            {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                    {error}
                </Alert>
            )}

            {result && (
                <Box sx={{ mt: 3 }}>
                    <Typography sx={{ color: theme.palette.text.primary, fontWeight: 600, mb: 1 }}>
                        Summary
                    </Typography>
                    <Box component="ul" sx={{ pl: 2.5, my: 0, color: theme.palette.text.primary }}>
                        <li><Typography variant="body2" component="span">CSV rows: {result.summary.csv_rows}</Typography></li>
                        <li><Typography variant="body2" component="span">Matched: {result.summary.matched}</Typography></li>
                        <li><Typography variant="body2" component="span">Parameter differences: {result.summary.param_diff}</Typography></li>
                        <li><Typography variant="body2" component="span">CSV only: {result.summary.csv_only}</Typography></li>
                        <li><Typography variant="body2" component="span">DB only: {result.summary.db_only}</Typography></li>
                        <li><Typography variant="body2" component="span">Ambiguous: {result.summary.ambiguous}</Typography></li>
                    </Box>

                    <Typography sx={{ color: theme.palette.text.primary, mt: 2, mb: 1, fontWeight: 600 }}>
                        Parameter differences
                    </Typography>
                    {result.param_diff.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">None.</Typography>
                    ) : (
                        <Table size="small" sx={{ maxWidth: "100%" }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Event code</TableCell>
                                    <TableCell>Par1</TableCell>
                                    <TableCell>Par2</TableCell>
                                    <TableCell>Par3</TableCell>
                                    <TableCell>Par4</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {result.param_diff.map((item, idx) => (
                                    <TableRow key={idx}>
                                        <TableCell>{item.key.event_code}</TableCell>
                                        {["Par1", "Par2", "Par3", "Par4"].map((p) => {
                                            const d = item.differences[p];
                                            return (
                                                <TableCell key={p}>
                                                    {d ? `${d.csv ?? ""} ≠ ${d.db ?? ""}` : "—"}
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}

                    <Typography sx={{ color: theme.palette.text.primary, mt: 2, mb: 1, fontWeight: 600 }} variant="subtitle2">
                        CSV only
                    </Typography>

                    {result.csv_only.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">None.</Typography>
                    ) : (
                        <Box component="ul" sx={{ pl: 2.5, mt: 0 }}>
                            {result.csv_only.map((row) => (
                                <li key={row.event_code}>
                                    <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
                                        {row.event_code}:{" "}
                                        {Object.entries(row.csv_params || {})
                                            .map(([k, v]) => `${k}=${v ?? ""}`)
                                            .join(", ")}
                                    </Typography>
                                </li>
                            ))}
                        </Box>
                    )}

                    <Typography sx={{ color: theme.palette.text.primary, mt: 2, mb: 1, fontWeight: 600 }} variant="subtitle2">
                        Matched
                    </Typography>

                    {result.matched.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">None.</Typography>
                    ) : (
                        <Box component="ul" sx={{ pl: 2.5, mt: 0 }}>
                            {result.matched.map((m) => (
                                <li key={`${m.key.event_code}-${m.db_version}-${m.db_contract}`}>
                                    <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
                                        Code {m.key.event_code} · Version {m.db_version} · Contract {m.db_contract}
                                    </Typography>
                                </li>
                            ))}
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );
};
